// Admin Users view — list accounts and assign subscription tiers.
// Follows the SystemModal table/pill/sort conventions (useTableSort) but
// renders as a full view like HistoryView. Only mounted for superusers
// (AppTopBar hides the tab; the backend enforces regardless).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminUser,
  Tier,
  TIERS,
  TIER_LABELS,
  listUsers,
  setUserTier,
} from 'api/adminApi';
import { ApiError } from 'api/restClient';
import { SortColumn, SortDir, useTableSort } from 'components/system/useTableSort';
import './usersView.css';

const USER_COLUMNS: SortColumn<AdminUser>[] = [
  { key: 'username', label: 'Username', accessor: (u) => u.username, type: 'string' },
  { key: 'email', label: 'Email', accessor: (u) => u.email, type: 'string' },
  { key: 'role', label: 'Role', accessor: (u) => u.role, type: 'string' },
  { key: 'tier', label: 'Tier', accessor: (u) => u.tier, type: 'string' },
  { key: 'active', label: 'Active', accessor: (u) => u.is_active, type: 'boolean' },
  { key: 'created', label: 'Created', accessor: (u) => u.created_at, type: 'string' },
];

function TierPill({ tier }: { tier: Tier }) {
  return <span className={`uv-pill uv-pill-${tier}`}>{TIER_LABELS[tier] ?? tier}</span>;
}

function SortHeader({
  label,
  colKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  colKey: string;
  active: boolean;
  dir: SortDir;
  onSort: (key: string) => void;
}) {
  return (
    <th
      className={`uv-th-sortable ${active ? 'uv-th-active' : ''}`}
      tabIndex={0}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(colKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(colKey);
        }
      }}
    >
      {label}
      <span className="uv-th-caret">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

/** One user row: tier select + save appears only when the value changed. */
function UserRow({
  user,
  onSaved,
}: {
  user: AdminUser;
  onSaved: (updated: AdminUser) => void;
}) {
  const [draft, setDraft] = useState<Tier>(user.tier);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-sync the draft when a refresh replaces the row data.
  useEffect(() => setDraft(user.tier), [user.tier]);

  const dirty = draft !== user.tier;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const updated = await setUserTier(user.id, draft);
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={user.is_active ? '' : 'uv-row-inactive'}>
      <td>
        {user.username}
        {user.is_superuser && <span className="uv-admin-tag">admin</span>}
      </td>
      <td className="uv-email">{user.email}</td>
      <td>{user.role}</td>
      <td>
        <TierPill tier={user.tier} />
      </td>
      <td>{user.is_active ? 'yes' : 'no'}</td>
      <td className="uv-date">{new Date(user.created_at).toLocaleDateString()}</td>
      <td className="uv-actions">
        <select
          className="uv-select"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value as Tier)}
        >
          {TIERS.map((t) => (
            <option key={t} value={t}>
              {TIER_LABELS[t]}
            </option>
          ))}
        </select>
        {dirty && (
          <button className="uv-savebtn" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
        {err && <span className="uv-err" title={err}>⚠ {err}</span>}
      </td>
    </tr>
  );
}

export function UsersView() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadErr(null);
    try {
      setUsers(await listUsers(0, 200, signal));
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        setLoadErr(e instanceof ApiError ? e.message : 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.tier.includes(q),
    );
  }, [users, filter]);

  const { sorted, sortKey, sortDir, sortBy } = useTableSort(filtered, USER_COLUMNS);

  const onSaved = (updated: AdminUser) =>
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));

  return (
    <div className="uv-root">
      <div className="uv-toolbar">
        <span className="uv-title">
          Users <span className="uv-count">{filtered.length}</span>
        </span>
        <input
          className="uv-filter"
          placeholder="Filter by username / email / tier…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button className="uv-refresh" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loadErr && <div className="uv-loaderr">{loadErr}</div>}

      {!loadErr && (
        <div className="uv-tablewrap">
          <table className="uv-table">
            <thead>
              <tr>
                {USER_COLUMNS.map((c) => (
                  <SortHeader
                    key={c.key}
                    label={c.label}
                    colKey={c.key}
                    active={sortKey === c.key}
                    dir={sortDir}
                    onSort={sortBy}
                  />
                ))}
                <th>Assign tier</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => (
                <UserRow key={u.id} user={u} onSaved={onSaved} />
              ))}
            </tbody>
          </table>
          {!loading && sorted.length === 0 && <div className="uv-empty">No users match.</div>}
        </div>
      )}
    </div>
  );
}
